import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Divider,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Game } from '@par/shared/types/game';
import { Profile } from '@par/shared/types/player';
import { fetchGet } from '@par/shared/api/common';

type ActiveSubTabs =
  | 'lounge'
  | 'teams'
  | 'standings'
  | 'players'
  | 'rules'
  | 'join'
  | 'manage';

type PropTypes = {
  profile: Profile;
};

export const LeagueSubNav = ({ profile }: PropTypes) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const getActiveTab = () => {
    if (location.pathname === '/league/lounge') return 'lounge';
    if (location.pathname.includes('/league/standings')) return 'standings';
    if (location.pathname.includes('/league/teams')) return 'teams';
    if (location.pathname.includes('/league/players')) return 'players';
    if (location.pathname.includes('/league/rules')) return 'rules';
    if (location.pathname.includes('/league/join')) return 'join';
    if (location.pathname.includes('/league/manage')) return 'manage';
  };

  const [activeSubTab, setActiveSubTab] = useState<ActiveSubTabs>(getActiveTab);

  useEffect(() => {
    setActiveSubTab(getActiveTab);
  }, [location]);

  const SubHeaderTab = (isActiveTab: boolean, text: string) => (
    <Typography
      variant={isActiveTab ? 'subtitle2led' : 'subtitle2'}
      letterSpacing={2}
      sx={{
        color: !isActiveTab ? theme.palette.ranked.main : '#48d2ff',
        filter: isActiveTab
          ? 'drop-shadow(0 0 15px #0266bd) drop-shadow(0 0 50px #0266bd) contrast(2)'
          : null,
      }}
    >
      {text}
    </Typography>
  );

  const handleLeagueLoungeClick = () => navigate('/league/lounge');
  const handleLeagueTeamsClick = () => navigate('/league/teams');
  const handleLeagueStandingsClick = () => navigate('/league/standings');
  const handleLeaguePlayersClick = () => navigate('/league/players');
  const handleLeagueRulesClick = () => navigate('/league/rules');
  const handleLeagueJoinClick = () => navigate('/league/join');
  const handleLeagueManageClick = () => navigate('/league/manage');

  const handleSubNavChange = (event: any, tab: ActiveSubTabs) => {
    setActiveSubTab(tab);
    if (tab === 'lounge') handleLeagueLoungeClick();
    if (tab === 'teams') handleLeagueTeamsClick();
    if (tab === 'standings') handleLeagueStandingsClick();
    if (tab === 'players') handleLeaguePlayersClick();
    if (tab === 'rules') handleLeagueRulesClick();
    if (tab === 'join') handleLeagueJoinClick();
    if (tab === 'manage') handleLeagueManageClick();
  };

  return (
    <Container>
      <Box display={'flex'} justifyContent={'left'} mt={2}>
        <Tabs
          variant="scrollable"
          value={false}
          onChange={handleSubNavChange}
          indicatorColor="secondary"
          sx={{
            width: '100%',
            height: 0,
            borderRadius: '10px',
            bgcolor: '#02071b',
            '& .MuiTabs-flexContainer': { height: '100%' },
          }}
        >
          <Tab
            label={SubHeaderTab(activeSubTab === 'lounge', 'Lounge')}
            value="lounge"
          />
          <Divider
            flexItem
            orientation="vertical"
            sx={{
              height: '40%',
              alignSelf: 'center',
              borderRightWidth: 2,
            }}
          />
          <Tab
            label={SubHeaderTab(activeSubTab === 'standings', 'Standings')}
            value="standings"
          />
          <Divider
            flexItem
            orientation="vertical"
            sx={{
              height: '40%',
              alignSelf: 'center',
              borderRightWidth: 2,
            }}
          />
          <Tab
            label={SubHeaderTab(activeSubTab === 'teams', 'Teams')}
            value="teams"
          />
          <Divider
            flexItem
            orientation="vertical"
            sx={{
              height: '40%',
              alignSelf: 'center',
              borderRightWidth: 2,
            }}
          />
          <Tab
            label={SubHeaderTab(activeSubTab === 'players', 'Players')}
            value="players"
          />
          <Divider
            flexItem
            orientation="vertical"
            sx={{
              height: '40%',
              alignSelf: 'center',
              borderRightWidth: 2,
            }}
          />
          <Tab
            label={SubHeaderTab(activeSubTab === 'rules', 'Rules')}
            value="rules"
          />
          <Divider
            flexItem
            orientation="vertical"
            sx={{
              height: '40%',
              alignSelf: 'center',
              borderRightWidth: 2,
            }}
          />
          <Tab
            label={SubHeaderTab(activeSubTab === 'join', 'How to join')}
            value="join"
          />
          {profile.admin ? (
            <Divider
              flexItem
              orientation="vertical"
              sx={{
                height: '40%',
                alignSelf: 'center',
                borderRightWidth: 2,
              }}
            />
          ) : null}
          {profile.admin ? (
            <Tab
              label={SubHeaderTab(activeSubTab === 'manage', 'Manage')}
              value="manage"
            />
          ) : null}
        </Tabs>
      </Box>
    </Container>
  );
};
